import { useEffect, useState, useMemo, PropsWithChildren } from 'react';

import moment from 'moment';

import { getLocaleFromPathname, captureSentryException, addBreadcrumb } from '@utils';
import { getBrandFromHostname } from '@utils/tenant';

import {
    TranslationProvider as PacklinkTranslationProvider,
    useTranslationProviderStatus,
} from '@packlink/translation-provider';
import { useTenantConfig, useTenantConfigStatus } from '@packlink/tenant-config-provider';
import { Application } from '@packlink/packlink-sdk';
import { useTenant } from '@packlink/tenant-provider';

export const TranslationProvider = ({ children }: PropsWithChildren<unknown>): JSX.Element => {
    const pathLocale = getLocaleFromPathname();
    const isPaypalBrand = getBrandFromHostname('paypal');
    const { loading: tenantConfigLoading, error: tenantConfigError } = useTenantConfigStatus();
    const { loading: translationsLoading, error: translationsError } = useTranslationProviderStatus();
    const [locale, setLocale] = useState<string | undefined>(undefined);
    const tenantConfig = useTenantConfig<ITenantConfig>();
    const tenant = useTenant();

    if (tenantConfigError || translationsError) {
        addBreadcrumb('info', 'Initialize TranslationProvider');
        tenantConfigError && captureSentryException(tenantConfigError);
        translationsError && captureSentryException(translationsError);
    }

    const isLoading = useMemo(
        () => tenantConfigLoading && translationsLoading,
        [tenantConfigLoading, translationsLoading],
    );

    useEffect(() => {
        if (!isLoading && !locale) {
            setLocale(tenantConfig.defaultLanguage);
            moment.locale(tenantConfig.defaultLanguage);
        }
    }, [isLoading, tenantConfig, pathLocale, locale]);

    // TODO: https://packlink.atlassian.net/browse/IDE-93
    useEffect(() => {
        if (tenantConfigError && !isLoading && tenant.name === null && isPaypalBrand) {
            window.location.replace('https://www.paypal.com');
        }
    }, [isLoading, isPaypalBrand, tenant, tenantConfigError]);

    return locale && !isLoading ? (
        <PacklinkTranslationProvider cdn={config.cdn.url} locale={locale} application={Application.SINGLE_FLOW}>
            {children}
        </PacklinkTranslationProvider>
    ) : (
        <></>
    );
};
